/* eslint-disable react/react-in-jsx-scope */
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import { isSnykAvailable } from 'backstage-plugin-snyk';
import { usePermission } from '@backstage/plugin-permission-react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { CodeSnippet, WarningPanel } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import { createTwoFilesPatch } from 'diff';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  catalogAnnotationsApiRef,
  type CatalogAnnotationsResolveResponse,
} from './catalogAnnotationsApi';
import {
  catalogAnnotationsApplyPermission,
  catalogAnnotationsResolvePermission,
} from './permissions';

const SNYK_ORG = 'snyk.io/org-id';
const SNYK_TARGET = 'snyk.io/target-id';
const JIRA_PROJECT = 'jira/project-key';
const JIRA_COMPONENT = 'jira/component';
const PD_SERVICE = 'pagerduty.com/service-id';

const SNYK_KEYS = [SNYK_ORG, SNYK_TARGET] as const;
const JIRA_KEYS = [JIRA_PROJECT, JIRA_COMPONENT] as const;
const PD_KEYS = [PD_SERVICE] as const;

const RESOLVE_DEBOUNCE_MS = 350;

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  expansionPanel: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  diffBox: {
    position: 'relative',
    minHeight: theme.spacing(12),
    marginTop: theme.spacing(2),
  },
}));

function computeMissingKeys(entity: Entity): string[] {
  const ann = entity.metadata.annotations ?? {};
  const missing: string[] = [];

  if (!isSnykAvailable(entity)) {
    missing.push(SNYK_ORG, SNYK_TARGET);
  }
  if (!ann[JIRA_PROJECT]) {
    missing.push(JIRA_PROJECT);
  }
  if (!ann[JIRA_COMPONENT]) {
    missing.push(JIRA_COMPONENT);
  }
  if (!ann[PD_SERVICE]) {
    missing.push(PD_SERVICE);
  }

  return Array.from(new Set(missing));
}

function selectedKeysList(selected: Record<string, boolean>): string[] {
  return Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);
}

function buildAnnotationOverrides(
  selected: Record<string, boolean>,
  snykOrgId: string,
  snykTargetId: string,
  jiraProjectKeys: string,
  jiraComponent: string,
  pdServiceId: string,
): Record<string, string> {
  const keys = selectedKeysList(selected);
  const o: Record<string, string> = {};
  if (keys.includes(SNYK_ORG) && snykOrgId.trim()) {
    o[SNYK_ORG] = snykOrgId.trim();
  }
  if (keys.includes(SNYK_TARGET) && snykTargetId.trim()) {
    o[SNYK_TARGET] = snykTargetId.trim();
  }
  if (keys.includes(JIRA_PROJECT) && jiraProjectKeys.trim()) {
    o[JIRA_PROJECT] = jiraProjectKeys.trim();
  }
  if (keys.includes(JIRA_COMPONENT) && jiraComponent.trim()) {
    o[JIRA_COMPONENT] = jiraComponent.trim();
  }
  if (keys.includes(PD_SERVICE) && pdServiceId.trim()) {
    o[PD_SERVICE] = pdServiceId.trim();
  }
  return o;
}

export function EntityCatalogAnnotationsBanner() {
  const classes = useStyles();
  const { entity } = useEntity();
  const alertApi = useApi(alertApiRef);
  const api = useApi(catalogAnnotationsApiRef);

  const entityRef = stringifyEntityRef(entity);

  const { allowed: canResolve } = usePermission({
    permission: catalogAnnotationsResolvePermission,
    resourceRef: entityRef,
  });
  const { allowed: canApply } = usePermission({
    permission: catalogAnnotationsApplyPermission,
    resourceRef: entityRef,
  });

  const missingKeys = useMemo(() => computeMissingKeys(entity), [entity]);

  const snykKeysMissing = useMemo(
    () => missingKeys.filter(k => (SNYK_KEYS as readonly string[]).includes(k)),
    [missingKeys],
  );
  const jiraKeysMissing = useMemo(
    () => missingKeys.filter(k => (JIRA_KEYS as readonly string[]).includes(k)),
    [missingKeys],
  );
  const pdKeysMissing = useMemo(
    () => missingKeys.filter(k => (PD_KEYS as readonly string[]).includes(k)),
    [missingKeys],
  );

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [branchName, setBranchName] = useState('cerebro/update-catalog-info');
  const [baseYaml, setBaseYaml] = useState('');
  const [previewYaml, setPreviewYaml] = useState('');
  const [diffText, setDiffText] = useState('');
  const [catalogInfoPath, setCatalogInfoPath] = useState('catalog-info.yaml');
  const [applyLoading, setApplyLoading] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [jiraProjectKeys, setJiraProjectKeys] = useState('');
  const [jiraComponent, setJiraComponent] = useState(entity.metadata.name);
  const [snykOrgId, setSnykOrgId] = useState('');
  const [snykTargetId, setSnykTargetId] = useState('');
  const [pdServiceId, setPdServiceId] = useState('');
  const [resolveNote, setResolveNote] = useState<string | undefined>();

  const seededFromServerRef = useRef(false);

  const applySeedFromResponse = useCallback(
    (res: {
      suggestions: {
        snyk?: {
          orgId?: string;
          suggestedTargetId?: string;
        };
        pagerduty: { services: Array<{ id: string }> };
        jira: { defaultComponent: string };
      };
    }) => {
      if (!seededFromServerRef.current) {
        seededFromServerRef.current = true;
        if (res.suggestions.snyk?.orgId) {
          setSnykOrgId(res.suggestions.snyk.orgId);
        }
        if (res.suggestions.snyk?.suggestedTargetId) {
          setSnykTargetId(res.suggestions.snyk.suggestedTargetId);
        }
        setJiraComponent(res.suggestions.jira.defaultComponent);
        if (res.suggestions.pagerduty.services[0]) {
          setPdServiceId(res.suggestions.pagerduty.services[0].id);
        }
      }
    },
    [],
  );

  const applyResolveResponse = useCallback(
    (res: CatalogAnnotationsResolveResponse) => {
      applySeedFromResponse(res);
      setBaseYaml(res.baseYaml);
      setPreviewYaml(res.previewYaml);
      setResolveNote(res.targetResolutionNote);
      const path = res.parsedRepo.catalogInfoPath || 'catalog-info.yaml';
      setCatalogInfoPath(path);
      if (res.baseYaml === res.previewYaml) {
        setDiffText('');
      } else {
        setDiffText(
          createTwoFilesPatch(
            `a/${path}`,
            `b/${path}`,
            res.baseYaml,
            res.previewYaml,
            '',
            '',
          ),
        );
      }
    },
    [applySeedFromResponse],
  );

  const runResolve = useCallback(async () => {
    const keys = selectedKeysList(selected);
    if (keys.length === 0) {
      setBaseYaml('');
      setPreviewYaml('');
      setDiffText('');
      setResolveError(null);
      setResolveLoading(false);
      return;
    }

    setResolveLoading(true);
    setResolveError(null);
    try {
      const res = await api.resolve({
        entityRef,
        selectedKeys: keys,
        annotationOverrides: buildAnnotationOverrides(
          selected,
          snykOrgId,
          snykTargetId,
          jiraProjectKeys,
          jiraComponent,
          pdServiceId,
        ),
      });
      applyResolveResponse(res);
    } catch (e: unknown) {
      setResolveError(e instanceof Error ? e.message : String(e));
      setBaseYaml('');
      setPreviewYaml('');
      setDiffText('');
    } finally {
      setResolveLoading(false);
    }
  }, [
    api,
    applyResolveResponse,
    entityRef,
    jiraComponent,
    jiraProjectKeys,
    pdServiceId,
    selected,
    snykOrgId,
    snykTargetId,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const keys = selectedKeysList(selected);
    if (keys.length === 0) {
      setBaseYaml('');
      setPreviewYaml('');
      setDiffText('');
      setResolveError(null);
      setResolveLoading(false);
      return undefined;
    }

    let cancelled = false;
    setResolveLoading(true);
    setResolveError(null);

    const timer = window.setTimeout(async () => {
      try {
        const res = await api.resolve({
          entityRef,
          selectedKeys: keys,
          annotationOverrides: buildAnnotationOverrides(
            selected,
            snykOrgId,
            snykTargetId,
            jiraProjectKeys,
            jiraComponent,
            pdServiceId,
          ),
        });
        if (cancelled) {
          return;
        }

        applyResolveResponse(res);
      } catch (e: unknown) {
        if (cancelled) {
          return;
        }
        setResolveError(e instanceof Error ? e.message : String(e));
        setBaseYaml('');
        setPreviewYaml('');
        setDiffText('');
      } finally {
        if (!cancelled) {
          setResolveLoading(false);
        }
      }
    }, RESOLVE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    selected,
    snykOrgId,
    snykTargetId,
    jiraProjectKeys,
    jiraComponent,
    pdServiceId,
    entityRef,
    api,
    applyResolveResponse,
  ]);

  const openDialog = useCallback(() => {
    const init: Record<string, boolean> = {};
    for (const k of missingKeys) {
      init[k] = true;
    }
    setSelected(init);
    setJiraComponent(entity.metadata.name);
    setBaseYaml('');
    setPreviewYaml('');
    setDiffText('');
    setResolveError(null);
    seededFromServerRef.current = false;
    setSnykOrgId('');
    setSnykTargetId('');
    setPdServiceId('');
    setJiraProjectKeys('');
    setResolveLoading(false);
    setOpen(true);
  }, [entity.metadata.name, missingKeys]);

  const handleRefreshPreview = useCallback(() => {
    void runResolve();
  }, [runResolve]);

  const handleApply = useCallback(async () => {
    const annotations = buildAnnotationOverrides(
      selected,
      snykOrgId,
      snykTargetId,
      jiraProjectKeys,
      jiraComponent,
      pdServiceId,
    );
    if (Object.keys(annotations).length === 0) {
      alertApi.post({
        message: 'Fill in annotation values before opening a PR',
        severity: 'warning',
      });
      return;
    }
    setApplyLoading(true);
    try {
      const res = await api.apply({
        entityRef,
        branchName,
        annotations,
      });
      alertApi.post({
        message: `Pull request opened: ${res.url}`,
        severity: 'success',
        display: 'transient',
      });
      setOpen(false);
    } catch (e: unknown) {
      alertApi.post({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    } finally {
      setApplyLoading(false);
    }
  }, [
    alertApi,
    api,
    branchName,
    entityRef,
    jiraComponent,
    jiraProjectKeys,
    pdServiceId,
    selected,
    snykOrgId,
    snykTargetId,
  ]);

  const selectedCount = selectedKeysList(selected).length;
  const canSubmitPr =
    Boolean(previewYaml) &&
    !resolveLoading &&
    !applyLoading &&
    !resolveError &&
    selectedCount > 0;

  if (missingKeys.length === 0) {
    return null;
  }

  return (
    <>
      <Grid item xs={12}>
        <WarningPanel
          title="Recommended catalog annotations are missing"
          message={`Missing: ${missingKeys.join(', ')}.`}
        >
          <Box mt={1}>
            {canResolve ? (
              <Button color="primary" variant="contained" onClick={openDialog}>
                Fix annotations
              </Button>
            ) : (
              <Typography variant="body2">
                You do not have permission to run the annotation helper for this
                entity.
              </Typography>
            )}
          </Box>
        </WarningPanel>
      </Grid>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Catalog annotations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Select annotations to add and adjust values. The preview updates as you
            change options; merged into <code>catalog-info.yaml</code> on the default branch (
            {catalogInfoPath}
            ).
          </Typography>

          <TextField
            className={classes.field}
            label="Branch name for pull request"
            fullWidth
            value={branchName}
            onChange={e => setBranchName(e.target.value)}
            helperText="Default: cerebro/update-catalog-info"
          />

          {snykKeysMissing.length > 0 ? (
            <ExpansionPanel
              className={classes.expansionPanel}
              defaultExpanded
            >
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Snyk</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Box width="100%">
                  {snykKeysMissing.map(k => (
                    <Box key={k} mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(selected[k])}
                            onChange={(_, c) =>
                              setSelected(s => ({ ...s, [k]: c }))
                            }
                          />
                        }
                        label={k}
                      />
                      <Collapse
                        in={Boolean(selected[k])}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box pl={3} pt={1}>
                          {k === SNYK_ORG ? (
                            <TextField
                              className={classes.field}
                              label="Snyk org id"
                              fullWidth
                              value={snykOrgId}
                              onChange={e => setSnykOrgId(e.target.value)}
                              helperText="Mapped from catalogAnnotations.snyk.orgIdsByHost for GitHub"
                            />
                          ) : null}
                          {k === SNYK_TARGET ? (
                            <TextField
                              className={classes.field}
                              label="Snyk target id"
                              fullWidth
                              value={snykTargetId}
                              onChange={e => setSnykTargetId(e.target.value)}
                              helperText={
                                resolveNote ??
                                'Use org/repo for GitHub, or a Snyk target UUID from your org.'
                              }
                            />
                          ) : null}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ) : null}

          {jiraKeysMissing.length > 0 ? (
            <ExpansionPanel
              className={classes.expansionPanel}
              defaultExpanded
            >
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Jira</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Box width="100%">
                  {jiraKeysMissing.map(k => (
                    <FormControlLabel
                      key={k}
                      control={
                        <Checkbox
                          checked={Boolean(selected[k])}
                          onChange={(_, c) =>
                            setSelected(s => ({ ...s, [k]: c }))
                          }
                        />
                      }
                      label={k}
                    />
                  ))}
                  <Collapse
                    in={
                      Boolean(selected[JIRA_PROJECT]) ||
                      Boolean(selected[JIRA_COMPONENT])
                    }
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box pl={3} pt={1}>
                      {selected[JIRA_PROJECT] ? (
                        <TextField
                          className={classes.field}
                          label="Jira project key(s)"
                          fullWidth
                          value={jiraProjectKeys}
                          onChange={e => setJiraProjectKeys(e.target.value)}
                          helperText="Comma-separated project keys (e.g. DEVEX)"
                        />
                      ) : null}
                      {selected[JIRA_COMPONENT] ? (
                        <TextField
                          className={classes.field}
                          label="Jira component"
                          fullWidth
                          value={jiraComponent}
                          onChange={e => setJiraComponent(e.target.value)}
                        />
                      ) : null}
                    </Box>
                  </Collapse>
                </Box>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ) : null}

          {pdKeysMissing.length > 0 ? (
            <ExpansionPanel
              className={classes.expansionPanel}
              defaultExpanded
            >
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">PagerDuty</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Box width="100%">
                  {pdKeysMissing.map(k => (
                    <Box key={k} mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(selected[k])}
                            onChange={(_, c) =>
                              setSelected(s => ({ ...s, [k]: c }))
                            }
                          />
                        }
                        label={k}
                      />
                      <Collapse
                        in={Boolean(selected[k])}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box pl={3} pt={1}>
                          <TextField
                            className={classes.field}
                            label="PagerDuty service id"
                            fullWidth
                            value={pdServiceId}
                            onChange={e => setPdServiceId(e.target.value)}
                            helperText="PagerDuty service identifier (e.g. PE7J3MH)"
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ) : null}

          <Box className={classes.diffBox}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle2">catalog-info.yaml preview</Typography>
              {resolveLoading ? <CircularProgress size={22} /> : null}
            </Box>
            {resolveError ? (
              <Typography color="error" variant="body2">
                {resolveError}
              </Typography>
            ) : null}
            {!resolveLoading &&
            !resolveError &&
            selectedCount > 0 &&
            baseYaml === previewYaml ? (
              <Typography variant="body2" color="textSecondary">
                No annotation changes yet (add values or select more annotations).
              </Typography>
            ) : null}
            {!resolveLoading && !resolveError && diffText ? (
              <CodeSnippet
                text={diffText}
                language="diff"
                showCopyCodeButton
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRefreshPreview}
            disabled={resolveLoading || selectedCount === 0}
            color="primary"
          >
            Refresh preview
          </Button>
          <Button
            onClick={handleApply}
            disabled={!canApply || !canSubmitPr}
            color="primary"
            variant="contained"
          >
            Open pull request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
